/* eslint-disable react-hooks/exhaustive-deps */
import { push } from '@lagunovsky/redux-react-router'
import HomeAction from 'home/actions/HomeAction'
import { omit, orderBy, uniqBy, uniq, compact, isEqual, difference, groupBy } from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import Checkbox from '../../../components/forms/Checkbox'
import Switch from '../../../components/forms/Switch'
import { PATH_ADMINISTRATION, PATH_ADMINISTRATION_CARTOGRAPHY } from '../../../home/constants/RouteConstants'
import { getLabel } from '../../../utils/StoreUtils'
import AdministrationAction from '../../actions/AdministrationAction'
import { LAYER_TYPES } from '../../constants/AdministrationConstants'
import { DEFAULT_MAP_BACKGROUND, MAP_BACKGROUNDS_TO_HIDE, homeListOfSelectedLayers } from 'carto/constants/CartoConstants'
import UserAction from '../user/actions/UserAction'
import LayerRightsModal from './LayerRightsModal'
import LayerTestPanel from './LayerTestPanel'
import MapAdminPanel from './MapAdminPanel'
import { Accordion, AccordionSummary, Card, CardContent, Grid, Tab, Tabs, Tooltip, Typography } from '@mui/material'
import Input from 'components/forms/Input'
import { NewTable } from '../../../components/datatable/NewTable'
import CloudOffIcon from '@mui/icons-material/CloudOff'
import { formatData } from '../../../utils/ExportDataUtil'
import ExportAction from '../../../export/actions/ExportAction'
import { RGBToHexColor } from '../../../utils/ColorUtil'
import useTitle from 'utils/customHook/useTitle'
import useActions from 'utils/customHook/useActions'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import ProgressCard from 'components/card/ProgressCard'
import VerticalSortableList from 'components/draggable/VerticalSortableList'
import { DragHandle, SortableItem } from 'components/draggable/components/sortableItems/SortableItem'
import PropTypes from 'prop-types'
import CartographyThemeDto from 'administration/dto/CartographyThemeDto'
import LayerDto from 'home/dto/LayerDto'
import { Edit, Person } from '@mui/icons-material'
import { BACKGROUNDS, getMapBackgrounds } from 'utils/mapUtils/MapBackgroundsUtils'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import { getSetting } from 'utils/SettingUtils'
import { lightGrey } from 'utils/constants/ColorTheme'
import { LastUpdateCard } from 'components/card/LastUpdateCard'
import ToastrAction from 'toastr/actions/ToastrAction'
import { normalizeString } from 'utils/StringUtil'

const TAB_THEME = 'themes'
const TAB_BACKGROUND = 'backgrounds'
const TAB_MAP = 'map'

const HEADERS_EXPORT = [
    'nameLayer',
    'color',
    'name',
    'type',
    'url',
    'code',
    'source',
    'projection',
]

const redirectToThemeForm = (id = 'new') => push(`${PATH_ADMINISTRATION}/${PATH_ADMINISTRATION_CARTOGRAPHY}/${id}`)

const AccordionTheme = ({
    isEditMode,
    theme,
    layers = [],
    searchValue,
}) => {
    const [openSelectUsersModal, setOpenSelectUsersModal] = useState(false)

    const {
        usersThemes,
        users,
        applicativeUsers,
    } = useSelector(store => ({
        usersThemes: store.UserReducer.usersThemes,
        users: store.UserReducer.users,
        applicativeUsers: store.UserReducer.applicativeUsers,
    }), shallowEqual)

    const dispatch = useDispatch()
    const allUsers = uniq([...users, ...applicativeUsers])
    const handleAssignTheme = (th, isChecked) => dispatch(UserAction.assignThemeToUsers(th.id, isChecked ? allUsers.map(o => o.login) : []))
    const selectedUsers = uniqBy(usersThemes.filter(ut => ut.themeId === theme.id), 'login')

    if (!layers.length && searchValue) {
        return null
    }

    return (
        <Grid container size={12} alignItems={'center'}>
            <Grid size={'grow'}>
                <Accordion key={theme.id} round>
                    <AccordionSummary round color={theme.color} sx={{ height: 20 }}>
                        <Grid container justifyContent='space-between' alignItems='center' sx={{ width: '100%' }} spacing={2}>
                            {
                                isEditMode && (
                                    <Grid size={1}>
                                        <Checkbox
                                            checked={selectedUsers.length > 0}
                                            className='white-checkbox'
                                            onChange={isChecked => handleAssignTheme(theme, isChecked)}
                                            containerStyle={{ display: 'flex', alignItems: 'center' }}
                                        />
                                    </Grid>
                                )
                            }
                            <Grid size='grow'>
                                {theme.name}
                            </Grid>
                            {isEditMode && !theme.isAquaDBTheme && (
                                <Grid>
                                    <Tooltip title={i18n.edit}>
                                        <Edit onClick={() => dispatch(redirectToThemeForm(theme.id))} />
                                    </Tooltip>
                                </Grid>
                            )}
                            {isEditMode && (
                                <Grid>
                                    <Tooltip title={i18n.restrictionByUser}>
                                        <Person onClick={(e) => {
                                            e.stopPropagation()
                                            setOpenSelectUsersModal(true)
                                        }}
                                        />
                                    </Tooltip>
                                </Grid>
                            )}
                            <Grid container justifyContent='flex-end'>
                                {selectedUsers.length} / {allUsers.length}
                            </Grid>
                        </Grid>
                    </AccordionSummary>
                    {layers.length ?
                        (
                            <NewTable headers={['name', 'type', { key: 'isToggled', value: '' }]} rows={orderBy(layers, 'ordre', 'desc')} />
                        ) : (
                            <Grid container alignItems={'center'} justifyContent={'center'} spacing={1}>
                                <CloudOffIcon />
                                <Typography variant='h6' fontWeight='bold'>{i18n.noLayers}</Typography>
                            </Grid>
                        )
                    }
                </Accordion>
            </Grid>
            <Grid>
                <DragHandle />
            </Grid>
            {theme && (
                <LayerRightsModal
                    theme={theme}
                    selectedUsers={selectedUsers}
                    open={openSelectUsersModal}
                    toggleModal={() => setOpenSelectUsersModal(!openSelectUsersModal)}
                />
            )}
        </Grid>
    )
}

AccordionTheme.propTypes = {
    isEditMode: PropTypes.bool,
    theme: PropTypes.instanceOf(CartographyThemeDto),
    layers: PropTypes.arrayOf(PropTypes.instanceOf(LayerDto)),
    searchValue: PropTypes.string,
}

const CartographyApp = () => {
    const [toggledLayers, setToggledLayers] = useState([])
    const [isEditMode, setIsEditMode] = useState(false)
    const [themes, setThemes] = useState([])
    const [searchValue, setSearchValue] = useState('')
    const [tab, setTab] = useState(TAB_THEME)
    const [paramsMap, setParamsMap] = useState()
    const [changeOrder, setChangeOrder] = useState(false)

    const {
        layers,
        adminThemes,
        users,
        applicativeUsers,
        accountUser,
        applicationSettings,
    } = useSelector(store => ({
        layers: store.AdministrationReducer.layers,
        adminThemes: store.AdministrationReducer.adminThemes,
        users: store.UserReducer.users,
        applicativeUsers: store.UserReducer.applicativeUsers,
        accountUser: store.AccountReducer.accountUser,
        applicationSettings: store.AdministrationReducer.applicationSettings,
    }), shallowEqual)

    const dispatch = useDispatch()

    useTitle(() => ([{
        title: i18n.administration,
        href: PATH_ADMINISTRATION,
    }, {
        title: i18n.cartography,
        href: `${PATH_ADMINISTRATION}/${PATH_ADMINISTRATION_CARTOGRAPHY}`,
    }]), [])

    const mapBackgroundsToHide = useApplicationSetting(MAP_BACKGROUNDS_TO_HIDE, s => compact(s?.split(',')?.map(b => b.trim()))) || []
    const defaultMapBackground = useApplicationSetting(DEFAULT_MAP_BACKGROUND) || 'OSM'

    const onChangeDefaultBackground = (backgroundName) => {
        const newSetting = [
            { parameter: DEFAULT_MAP_BACKGROUND, value: backgroundName },
        ].filter(p => getSetting(applicationSettings, p.parameter) !== p.value)

        dispatch(AdministrationAction.updateSieauParameters(newSetting))
    }
    const onChangeBackgroundsToHide = (backgroundName, isChecked) => {
        const newHides = isChecked ? mapBackgroundsToHide.filter(b => b !== backgroundName) : uniq([...mapBackgroundsToHide, backgroundName])
        const newSetting = [{
            parameter: MAP_BACKGROUNDS_TO_HIDE,
            value: newHides.join(','),
        }]
        if (newHides.length === getMapBackgrounds().length) { // si tout est masqué, on remet le fond blanc par défaut
            dispatch(ToastrAction.warning(i18n.youMustSelectAtLeastOneBackground))
            dispatch(AdministrationAction.updateSieauParameters([
                { parameter: DEFAULT_MAP_BACKGROUND, value: BACKGROUNDS.WHITE },
                { parameter: MAP_BACKGROUNDS_TO_HIDE, value: newHides.filter(b => b !== BACKGROUNDS.WHITE).join(',') },
            ]))
        } else if (newHides.includes(defaultMapBackground)) {
            dispatch(AdministrationAction.updateSieauParameters([
                { parameter: DEFAULT_MAP_BACKGROUND, value: getMapBackgrounds().filter(bg => !newHides.includes(bg.name))[0]?.name },
                ...newSetting,
            ]))
        } else {
            dispatch(AdministrationAction.updateSieauParameters(newSetting))
        }
    }

    const getBackgroundItem = (background) => {
        const isHidden = mapBackgroundsToHide.includes(background.name)
        return (
            <Grid size={6} offset={3}>
                <Card sx={{ backgroundColor: isHidden && lightGrey }}>
                    <CardContent>
                        <Grid
                            container
                            justifyContent='space-between'
                        >
                            <Grid size={1} container direction='column' justifyContent='center'>
                                <Switch
                                    checked={!isHidden}
                                    onChange={(isChecked) => onChangeBackgroundsToHide(background.name, isChecked)}
                                    tooltip={isHidden ? i18n.disabled : i18n.activated}
                                />
                            </Grid>
                            <Grid size='auto' container direction='column' alignItems='center' spacing={1} sx={{ height: '60px' }}>
                                <Typography variant='h5' sx={{ fontWeight: 'bold' }}>{background.label}</Typography>
                                <input
                                    className='sieau-input'
                                    type='radio'
                                    id={background.name}
                                    disabled={isHidden}
                                    checked={defaultMapBackground === background.name}
                                    onClick={() => onChangeDefaultBackground(background.name)}
                                />
                                <label htmlFor={background.name}>{i18n.displayByDefault}</label>
                            </Grid>
                            <Grid size={{ lg: 'auto', md: 12 }} sx={{ minWidth: '150px' }}>
                                {background.img && (
                                    <img
                                        src={background.img}
                                        alt={background.label}
                                        style={{
                                            width: 150,
                                            height: 'auto',
                                            maxHeight: 60,
                                            overflow: 'hidden',
                                            filter: isHidden ? 'grayscale(100%)' : 'none',
                                        }}
                                    />
                                )}
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>
        )
    }

    const onExport = (exportType) => {
        const exportTheme = orderBy(adminThemes, [t => !t.isAquaDBTheme, 'themeOrder'])
            .flatMap(adminTheme => {
                return orderBy(layers, ['name'])
                    .filter(layer => layer.theme === adminTheme.id)
                    .map(l => ({
                        headers: HEADERS_EXPORT,
                        nameLayer: adminTheme.name,
                        color: RGBToHexColor(adminTheme.color),
                        name: l.name,
                        type: getLabel(LAYER_TYPES, l.type),
                        url: l.url,
                        code: l.layer,
                        source: l.source,
                        projection: l.projection,
                    }))
            })
        dispatch(ExportAction.export(formatData(exportTheme), exportType, i18n.themesExport))
    }

    const saveParamsMap = () => {
        if (paramsMap) {
            dispatch(AdministrationAction.updateSieauParameters(paramsMap))
        }
        setIsEditMode(!isEditMode)
    }

    const saveOrderThemes = () => {
        AdministrationAction.updateCartographyThemes(themes).then(() => setChangeOrder(!changeOrder))
    }

    const selectedLayersParameter = useAccountSetting(homeListOfSelectedLayers)

    const { isLoaded, progress } = useProgressDispatch(() => compact([
        dispatch(AdministrationAction.fetchAdminCartographyThemes()),
        dispatch(AdministrationAction.fetchThemeLayers()),
        dispatch(UserAction.fetchUsersThemesAssignment()),
        (!users.length || !applicativeUsers.length) && dispatch(UserAction.fetchAllUsers()),
    ]), [changeOrder])

    const filteredThemes = useMemo(() => orderBy(adminThemes.filter(t => (!t.isAquaDBTheme || layers.find(l => t.id === l.theme))), 'themeOrder', 'desc'), [adminThemes, layers])

    useEffect(() => {
        setThemes(filteredThemes)
        dispatch(HomeAction.setHelpLink('administration', '90'))
        const selectedLayers = selectedLayersParameter?.length > 0 ? selectedLayersParameter.split(',').map(item => {
            return layers.find(l => l && l.id === parseInt(item))
        }).filter(layer => !!layer) : []
        setToggledLayers(selectedLayers)
    }, [isLoaded])

    useActions(() => {
        if (accountUser.consultant === '1') {
            return {}
        }
        const label = isEditMode ? 'cancel' : 'edit'
        const save = isLoaded && !isEqual(filteredThemes, themes) ? {
            save: saveOrderThemes,
        } : {}
        const actions = {
            new: () => dispatch(redirectToThemeForm()),
            [label]: () => setIsEditMode(!isEditMode),
            ...save,
            exportChoice: {
                exportChoices: [{
                    name: i18n.exportFull,
                    formats: [{
                        type: i18n.exportXLSX,
                        callback: () => onExport('xlsx'),
                    }, {
                        type: i18n.csv,
                        callback: () => onExport('csv'),
                    }],
                }],
            },
        }
        if (tab === TAB_THEME) {
            return actions
        } else if (tab === TAB_MAP) {
            if (isEditMode) {
                return {
                    [label]: () => setIsEditMode(!isEditMode),
                    save: () => saveParamsMap(),
                }
            }
            return omit(actions, ['new'])
        }
        return {}
    }, [isEditMode, tab, themes, filteredThemes, paramsMap])

    useEffect(() => {
        const layersId = toggledLayers.map(l => l.id)
        const layersIdString = layersId.join() || ''
        dispatch(AdministrationAction.updateSetting(homeListOfSelectedLayers, layersIdString))
    }, [toggledLayers])

    const aquadb = useMemo(() => orderBy(themes, 'themeOrder', 'desc').filter(th => th.isAquaDBTheme), [themes, layers])
    const other = useMemo(() => difference(orderBy(themes, 'themeOrder', 'desc'), aquadb), [themes, aquadb])
    const handleChangeOrder = (aquadbThemes, otherThemes) => {
        const allThemes = [...aquadbThemes, ...otherThemes]
        const orderedThemes = allThemes.map((th, i) => (new CartographyThemeDto({ ...th, themeOrder: allThemes.length - 1 - i })))
        setThemes(orderedThemes)
    }
    const toggleLayer = (layer, isChecked) => setToggledLayers((prevToggledLayers) => isChecked ? [...prevToggledLayers, layer] : prevToggledLayers.filter(l => l.id != layer.id))
    const groupedLayers = useMemo(() => {
        const formattedLayers = layers.filter(layer => normalizeString(layer.name).includes(normalizeString(searchValue))).map(l => ({
            ...l,
            isToggled: (
                <Switch
                    checked={toggledLayers.some(layer => layer.id === l.id)}
                    onChange={(isChecked) => toggleLayer(l, isChecked)}
                />
            ),
            type: getLabel(LAYER_TYPES, l.type),
        }))
        return groupBy(formattedLayers, 'theme')
    }, [layers, searchValue])

    if (!isLoaded) {
        return <Grid padding={1}><ProgressCard progress={progress} /></Grid>
    }

    return (
        <>
            <Tabs
                value={tab}
                onChange={(e, newValue) => setTab(newValue)}
                variant='fullWidth'
            >
                <Tab value={TAB_THEME} label={i18n.themes} disabled={isEditMode}/>
                <Tab value={TAB_BACKGROUND} label={i18n.backgroundLayers} disabled={isEditMode}/>
                <Tab value={TAB_MAP} label={i18n.settingDefaultView} disabled={isEditMode}/>
            </Tabs>
            <Card sx={{ borderRadius: '0 0 10px 10px' }}>
                <CardContent>
                    {tab === TAB_THEME && (
                        <Grid
                            container
                            alignItems='flex-start'
                            justifyContent='space-between'
                            px={'1rem'}
                        >
                            <Grid size={6}>
                                <Input
                                    title={i18n.search}
                                    value={searchValue}
                                    onChange={(v) => setSearchValue(v)}
                                />
                                {!!aquadb.length && (
                                    <>
                                        <h3>{i18n.openDataThemes}</h3>
                                        <VerticalSortableList
                                            items={aquadb}
                                            onChange={(newThemes) => handleChangeOrder(newThemes, other)}
                                            renderItem={theme => (
                                                <SortableItem id={theme.id}>
                                                    <AccordionTheme
                                                        isEditMode={isEditMode}
                                                        theme={theme}
                                                        layers={groupedLayers[theme.id] || []}
                                                        searchValue={searchValue}
                                                    />
                                                </SortableItem>
                                            )}
                                        />
                                    </>
                                )}
                                {!!other.length && (
                                    <>
                                        <h3>{i18n.privateThemes}</h3>
                                        <VerticalSortableList
                                            items={other}
                                            onChange={(newThemes) => handleChangeOrder(aquadb, newThemes)}
                                            renderItem={theme => (
                                                <SortableItem id={theme.id}>
                                                    <AccordionTheme
                                                        isEditMode={isEditMode}
                                                        theme={theme}
                                                        layers={groupedLayers[theme.id] || []}
                                                        searchValue={searchValue}
                                                    />
                                                </SortableItem>
                                            )}
                                        />
                                    </>
                                )}
                            </Grid>
                            <Grid size={6} sx={{ paddingTop: '1rem', paddingLeft: '1rem' }} container direction='column' alignItems='stretch' spacing={2}>
                                <LastUpdateCard
                                    element={orderBy(adminThemes.filter(t => t.updateDate), 'updateDate', 'desc')[0]}
                                    withHour
                                />
                                <LayerTestPanel layers={toggledLayers} />
                            </Grid>
                        </Grid>
                    )}
                    {tab === TAB_BACKGROUND && (
                        <Grid container spacing={2.5} alignItems='flex-end'>
                            {getMapBackgrounds().map(bg => getBackgroundItem(bg))}
                            <Grid size='auto' offset='auto'>
                                <LastUpdateCard
                                    element={orderBy([
                                        applicationSettings.find(s => s.parameter === DEFAULT_MAP_BACKGROUND) ?? {},
                                        applicationSettings.find(s => s.parameter === MAP_BACKGROUNDS_TO_HIDE) ?? {},
                                    ], 'updateDate', 'desc')[0]}
                                    withHour
                                />
                            </Grid>
                        </Grid>
                    )}
                    {tab === TAB_MAP && (
                        <MapAdminPanel
                            disabled={!isEditMode}
                            onChange={(params) => setParamsMap(params)}
                        />
                    )}
                </CardContent>
            </Card>
        </>
    )
}

export default CartographyApp